import React, { useRef } from 'react'
import cx from 'classnames'
import { useCombinedRefs } from 'hooks'
import { useEntry } from 'intersection-observer'
import { useZinaImage, useZinaStaticImage } from 'zina'
import { useDevice } from 'device'

import s from './Image.scss'


export type ImageProps = {
  className?: string
  src: string
  bgColor?: string
  aspect?: number
  withLoader?: boolean
  alt?: string
  title?: string
  fullWidth?: boolean
  lazy?: boolean
  style?: React.HTMLAttributes<HTMLImageElement>['style']
  'data-testid'?: string
}

type RenderImageProps = ImageProps & {
  nodeRef?: React.Ref<HTMLImageElement>
  isLoading?: boolean
}

const RenderImage: React.FunctionComponent<RenderImageProps> = (props) => {
  const {
    className, alt = '', title, fullWidth, 'data-testid': dataTestId,
    nodeRef, src, isLoading, style,
  } = props

  const nodeClassName = cx(className, {
    [s.loader]: isLoading,
    'w-full': fullWidth,
  })

  return (
    <img
      className={nodeClassName}
      ref={nodeRef}
      src={src}
      alt={alt}
      title={title}
      draggable={false}
      decoding="async"
      data-testid={dataTestId}
      style={style}
    />
  )
}

const LazyImage: React.FunctionComponent<ImageProps> = (props) => {
  const { src, bgColor, aspect } = props

  const [ ref, entry ] = useEntry({ once: true, observerProps: { rootMargin: '25%' } })
  const skip = !entry?.isIntersecting

  const nodeRef = useRef()
  const { zinaSrc, isLoading } = useZinaImage({ nodeRef, src, bgColor, aspect, withLoader: true, skip })

  const finalRef = useCombinedRefs([ ref, nodeRef ])

  return (
    <RenderImage {...props} src={zinaSrc} nodeRef={finalRef} isLoading={isLoading} />
  )
}

const DefaultImage: React.FunctionComponent<ImageProps> = (props) => {
  const { src, bgColor, aspect, withLoader = true } = props

  const nodeRef = useRef<HTMLImageElement>()
  const { zinaSrc, isLoading } = useZinaImage({ nodeRef, src, bgColor, aspect, withLoader })

  return (
    <RenderImage {...props} src={zinaSrc} nodeRef={nodeRef} isLoading={isLoading} />
  )
}

// special image for bot-view
const StaticImage: React.FunctionComponent<ImageProps> = (props) => {
  const { src, bgColor } = props

  const finalSrc = useZinaStaticImage({ src, bgColor })

  return (
    <RenderImage
      {...props}
      src={finalSrc}
    />
  )
}

const Image: React.FunctionComponent<ImageProps> = (props) => {
  const { isBot } = useDevice()
  const { lazy } = props

  if (isBot) {
    return (
      <StaticImage {...props} />
    )
  }

  return React.createElement(lazy ? LazyImage : DefaultImage, props)
}


export default React.memo(Image)
